import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs';

export interface PostHogUserInfo {
  userId?: string | number;
  companyId?: string | number;
}

export interface PostHogEventProperties {
  [key: string]: string | number | boolean | undefined | null;
}

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private initialized = false;
  private router = inject(Router);
  private currentUserInfo: PostHogUserInfo | null = null;

  /**
   * Initializes PostHog analytics with environment configuration.
   * Safe to call multiple times - will only initialize once.
   * Automatically disables session recording, performance tracking, and surveys.
   * Sets up automatic pageview tracking on route changes.
   */
  init(): void {
    if (this.initialized || !environment.posthog?.apiKey) {
      return;
    }

    this.cleanupOldPostHogCookies();

    posthog.init(environment.posthog.apiKey, {
      api_host: environment.posthog.apiHost || 'https://us.i.posthog.com',
      // Managed reverse proxy: api_host is our proxy domain, so ui_host must be the
      // real PostHog app URL for toolbar / "view in PostHog" links to resolve.
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      disable_session_recording: true,
      capture_performance: false,
      disable_surveys: true,
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: false,
    });

    this.initialized = true;
    this.setupRouteTracking();
  }

  /**
   * Sets up automatic pageview tracking on route navigation.
   * Tracks $pageview event whenever the route changes.
   */
  private setupRouteTracking(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.capturePageView(event.urlAfterRedirects);
      });
  }

  /**
   * Identifies a user in PostHog and stores user context for automatic enrichment.
   * @param userInfo - User identification information (userId and companyId)
   */
  identify(userInfo: PostHogUserInfo): void {
    if (!this.initialized) {
      return;
    }

    const distinctId = userInfo.userId?.toString();
    if (!distinctId) {
      return;
    }

    try {
      this.currentUserInfo = userInfo;

      posthog.identify(distinctId, {
        companyId: userInfo.companyId,
        userId: userInfo.userId,
      });
    } catch (error) {
      console.error('PostHog identify error:', error);
    }
  }

  /**
   * Resets PostHog user identity and clears stored user context.
   * Should be called on user logout to prevent identity merging.
   */
  reset(): void {
    if (!this.initialized) {
      return;
    }
    try {
      this.currentUserInfo = null;
      posthog.reset();
    } catch (error) {
      console.error('PostHog reset error:', error);
    }
  }

  /**
   * Captures a custom event with automatic enrichment of user context, timestamp, and route.
   * @param eventName - Name of the event to track
   * @param properties - Optional event-specific properties (userId, companyId, timestamp, and route are auto-added)
   */
  captureEvent(eventName: string, properties?: PostHogEventProperties): void {
    if (!this.initialized) {
      return;
    }

    try {
      const enrichedProperties: PostHogEventProperties = {
        ...properties,
        userId: this.currentUserInfo?.userId,
        companyId: this.currentUserInfo?.companyId,
        timestamp: new Date().toISOString(),
        route: this.router.url
      };

      posthog.capture(eventName, enrichedProperties);
    } catch (error) {
      console.error('PostHog capture error:', error);
    }
  }

  /**
   * Manually captures a page view event.
   * Automatically called on route changes.
   * @param path - Page path to track (will be converted to full URL)
   */
  capturePageView(path?: string): void {
    if (!this.initialized) {
      return;
    }
    try {
      const currentPath = path || this.router.url;
      const fullUrl = `${window.location.origin}${currentPath}`;
      
      const properties: PostHogEventProperties = {
        $current_url: fullUrl,
        userId: this.currentUserInfo?.userId,
        companyId: this.currentUserInfo?.companyId,
        timestamp: new Date().toISOString()
      };
      posthog.capture('$pageview', properties);
    } catch (error) {
      console.error('PostHog page view error:', error);
    }
  }

  /**
   * One-time cleanup: remove old PostHog cookies set on the root domain
   * (.sarasanalytics.com) from before cross_subdomain_cookie was disabled.
   */
  private cleanupOldPostHogCookies(): void {
    try {
      const rootDomain = '.sarasanalytics.com';
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.startsWith('ph_') && name.endsWith('_posthog')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${rootDomain}; path=/`;
        }
      });
    } catch (e) {
      // Ignore cleanup errors
    }
  }

  /**
   * Sets additional properties on the current user's profile.
   * @param properties - User properties to set
   */
  setUserProperties(properties: PostHogEventProperties): void {
    if (!this.initialized) {
      return;
    }
    try {
      posthog.setPersonProperties(properties);
    } catch (error) {
      console.error('PostHog set properties error:', error);
    }
  }
}
